import type { GlobalShortcutsItem, ProductLink } from '@atlassian/global-shortcuts';

import {
	INVITED_APP_SHORTCUTS_ALLOWED_SOURCE_APPS,
	INVITED_APP_SHORTCUTS_ALLOWED_TARGET_APPS,
	type InvitedAppShortcutAllowedSourceAppKey,
	type InvitedAppShortcutAllowedTargetAppKey,
	type InvitedAppShortcutVariant,
} from '../../common/types';

export type GlobalShortcutsItemMap = {
	[key: string]: GlobalShortcutsItem;
};

export const ProductsInState: Set<'jira' | 'confluence' | 'home'> = new Set([
	...INVITED_APP_SHORTCUTS_ALLOWED_SOURCE_APPS,
	...INVITED_APP_SHORTCUTS_ALLOWED_TARGET_APPS,
]);
export type ProductInState =
	| InvitedAppShortcutAllowedTargetAppKey
	| InvitedAppShortcutAllowedSourceAppKey;

export const IneligibilityReason = {
	EXPERIMENT_OFF: 'EXPERIMENT_OFF',
	ERROR: 'ERROR',
	CACHE_EMPTY: 'CACHE_EMPTY',
	CACHE_EMPTY_USER_MISMATCH: 'CACHE_EMPTY_USER_MISMATCH',
	CACHE_EXPIRED: 'CACHE_EXPIRED',
	NO_TARGET_PRODUCTS: 'NO_TARGET_PRODUCTS',
	NO_ACCESS_TO_TARGET_PRODUCTS: 'NO_ACCESS_TO_TARGET_PRODUCTS',
	TARGET_PRODUCTS_NOT_VISIBLE: 'TARGET_PRODUCTS_NOT_VISIBLE',
	TARGET_PRODUCTS_ALL_ACTIVE: 'TARGET_PRODUCTS_ALL_ACTIVE',
	SOURCE_PRODUCT_TOO_NEW: 'SOURCE_PRODUCT_TOO_NEW',
	/**
	 * Returned in `mode: 'productionisation'` when the caller passes an
	 * empty `recommendedApps` array — no target product was recommended.
	 */
	NO_RECOMMENDED_APPS: 'NO_RECOMMENDED_APPS',
} as const;
export type IneligibilityReason = (typeof IneligibilityReason)[keyof typeof IneligibilityReason];

export type ExperimentDetails = IneligibleExperimentDetails | EligibleExperimentDetails;

export type IneligibleExperimentDetails = {
	ineligibilityReason: IneligibilityReason;
	invitedApps?: undefined;
};

export type EligibleExperimentDetails = {
	ineligibilityReason: null;
	invitedApps: {
		[app in InvitedAppShortcutAllowedTargetAppKey]?: InvitedAppDetails;
	};
};

export type InvitedAppDetails = {
	/**
	 * Legacy cookie-cache collaborator snapshot. In `mode: 'productionisation'`
	 * this is `undefined` — collaborators are fetched lazily on hover via the
	 * Relay `InvitedAppShortcutsCollaboratorsQuery` query and read by the
	 * Relay inner container instead. To be tidied up after productionisation
	 * is fully rolled out.
	 */
	collaborators?: Array<{ avatar: string } | null>;
	eligible: true;
	/**
	 * Legacy cookie-cache impression timestamp. In `mode: 'productionisation'`
	 * this is `undefined` — GRS resolves impression state from the TAP
	 * `{product}_invited_app_shortcuts_first_impression_at` trait and delivers the
	 * outcome as `variant` below. To be tidied up after productionisation is
	 * fully rolled out.
	 */
	firstImpressionAt?: StoredTimestamp | null;
	/**
	 * Lozenge variant for this app, parsed from the GRS recommendation
	 * `entityId` in `mode: 'productionisation'`.
	 *
	 * Always populated by `resolveFromRecommendations` — `'none'` means "render
	 * the shortcut without a lozenge", which is the fallback for plain-product
	 * ids and unrecognised variants, so there is no absent case to handle.
	 */
	variant: InvitedAppShortcutVariant;
};

export const CACHE_WRITE_ID_KEY = 'updateId';

export const LOST_ACCESS_CHECK_DEBOUNCE_TIMEOUT = 5000;

export const FirstActiveSpecialValues = {
	NOT_CHECKED: undefined,
	INACTIVE: null,
};
export type FirstActiveSpecialValues =
	(typeof FirstActiveSpecialValues)[keyof typeof FirstActiveSpecialValues];

export type CachedState = {
	apps?: {
		[app in ProductInState]?: CachedAppState;
	};

	// Internal field.
	// Used to check if we can write to the cache, or if it's blocked/full.
	// We'll try to read this field, increment it, write, and read it back.
	[CACHE_WRITE_ID_KEY]?: number;

	firstActiveUpdatedTimestamp?: StoredTimestamp;
	uid?: string | null; // AAID for whom the data was written
};

export type CachedAppState = {
	access?: boolean; // From Nav (Switcher) BFF. Does the user have access to this app?
	firstActive?: StoredTimestamp | FirstActiveSpecialValues; // From Site User Traits. null == checked but not active
	firstImpressionAt?: StoredTimestamp;
	productLinks?: ProductLink[]; // Hydrated from nav product links and reused before re-hydration.
	visible?: boolean; // From Nav Configuration
} &
	// From CollabGraph:
	(
		| {
				collaboratorCount: number;
				collaboratorDetails: { avatar: string }[];
		  }
		| {
				collaboratorCount?: undefined;
				collaboratorDetails?: undefined;
		  }
	);

export type FullContext = {
	cloudId: string;
	enabled: boolean;
	sourceProduct: InvitedAppShortcutAllowedSourceAppKey;

	targetProducts: ReadonlyArray<InvitedAppShortcutAllowedTargetAppKey>;

	/**
	 * Allow null only because the Jira tenant context hook declares that it may return it.
	 */
	userId: string | null;
};

export type StoredTimestamp = number;

export type Trait = { name: string; value: string | boolean | number };
export type Traits = Trait[];
export interface InvitedAppShortcutsCache {
	read: ({ ssr }: { ssr: boolean }) => CachedState | null;
	write: (state: CachedState) => Promise<boolean>;
}
